"use client";

import { CalendarCheck, ClipboardCheck, Dumbbell, Home } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";
import { ctaLinks, images } from "./site-content";

const centerHighlights = [
  { icon: CalendarCheck, label: "예약제 1:1 운영" },
  { icon: Dumbbell, label: "개인별 재활운동" },
  { icon: ClipboardCheck, label: "평가 기반 운동 설계" },
  { icon: Home, label: "일상 동작 회복 중심" },
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.16,
    },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export function CenterIntroSection({ className }: { className?: string }) {
  return (
    <motion.section
      className={cn(
        "relative flex w-full flex-col overflow-hidden bg-[#fffdf2] text-[#223657] md:min-h-[680px] md:flex-row",
        className,
      )}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.28 }}
      variants={containerVariants}
    >
      <div className="flex w-full flex-col justify-between px-5 py-14 md:w-1/2 md:px-10 md:py-16 lg:w-3/5 lg:px-16 lg:py-20">
        <div>
          <motion.header className="mb-10 md:mb-14" variants={itemVariants}>
            <div className="flex items-center gap-3">
              <span className="inline-flex size-11 items-center justify-center rounded-full bg-[#ffc917] text-base font-black text-[#223657]">
                M
              </span>
              <div>
                <p className="text-lg font-black leading-tight text-[#223657]">
                  MOTIONA 재활운동센터
                </p>
                <p className="mt-1 text-xs font-bold text-[#69717d]">
                  PRIVATE CENTER PROGRAM
                </p>
              </div>
            </div>
          </motion.header>

          <motion.main variants={containerVariants}>
            <motion.h2
              className="text-4xl font-black leading-[1.05] text-[#223657] md:text-6xl"
              variants={itemVariants}
            >
              센터 재활운동
            </motion.h2>
            <motion.div
              className="my-6 h-1 w-28 bg-[#ffc917]"
              variants={itemVariants}
            />
            <motion.p
              className="max-w-[560px] text-xl font-black leading-9 text-[#69717d] md:text-2xl"
              variants={itemVariants}
            >
              예약제로 운영되는 공간에서 한 사람의 움직임에 집중합니다.
            </motion.p>
            <motion.a
              href={ctaLinks.naverBooking}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex min-h-12 items-center justify-center rounded-full bg-[#223657] px-6 text-sm font-black text-[#fffdf2] transition hover:-translate-y-0.5 hover:bg-[#172845]"
              variants={itemVariants}
            >
              첫 평가 예약하기
            </motion.a>
          </motion.main>
        </div>

        <motion.footer className="mt-12 w-full" variants={itemVariants}>
          <div className="grid grid-cols-1 gap-4 text-sm font-black text-[#223657] sm:grid-cols-2">
            {centerHighlights.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.label} className="flex min-h-12 items-center gap-3">
                  <Icon className="size-5 shrink-0 text-[#ffc917]" aria-hidden="true" />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </motion.footer>
      </div>

      <motion.div
        className="min-h-[340px] w-full bg-cover bg-center md:min-h-[680px] md:w-1/2 lg:w-2/5"
        style={{
          backgroundImage: `url(${images.centerIntroSpace.src})`,
          clipPath: "polygon(25% 0, 100% 0, 100% 100%, 0% 100%)",
        }}
        initial={{
          clipPath: "polygon(62% 0, 100% 0, 100% 100%, 42% 100%)",
          opacity: 0.82,
          scale: 1.035,
        }}
        whileInView={{
          clipPath: "polygon(25% 0, 100% 0, 100% 100%, 0% 100%)",
          opacity: 1,
          scale: 1,
        }}
        viewport={{ once: true, amount: 0.28 }}
        transition={{ duration: 1.15, ease: "circOut" }}
        role="img"
        aria-label={images.centerIntroSpace.alt}
      />
    </motion.section>
  );
}
